/**
 * Hook de sécurité pour les formulaires
 * 
 * @description Protection CSRF, validation et rate limiting pour les soumissions de formulaires
 * @since 2026-01-13
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  setCsrfToken, 
  getCsrfToken, 
  checkRateLimit,
  containsMaliciousContent,
  isValidEmail,
  isValidPhoneNumber,
  isValidAmount
} from '../utils/security';
import { detectSqlInjection, detectPromptInjection, sanitizeFormData } from '../utils/sanitize';

interface FormSecurityOptions {
  /** Activer la protection CSRF */
  enableCsrf?: boolean;
  /** Activer la détection d'injection SQL */
  detectSqlInjection?: boolean;
  /** Activer la détection d'injection de prompt */
  detectPromptInjection?: boolean;
  /** Activer le rate limiting */
  enableRateLimit?: boolean;
  /** Identifiant pour le rate limiting (ex: userId) */
  rateLimitIdentifier?: string;
  /** Action pour le rate limiting */
  rateLimitAction?: string;
  /** Callback lors d'une tentative d'attaque */
  onSecurityViolation?: (type: string, details: string) => void;
}

interface FormSecurityResult {
  /** Token CSRF à inclure dans le formulaire */
  csrfToken: string | null;
  /** Valide les données du formulaire avant soumission */
  validateFormSecurity: (data: object) => ValidationResult;
  /** Sanitise les données du formulaire */
  sanitizeFormInput: <T extends object>(data: T) => T;
  /** Indique si une soumission est en cours (anti-double soumission) */
  isSubmitting: boolean;
  /** Démarre la soumission (anti-double soumission) */
  startSubmit: () => boolean;
  /** Termine la soumission */
  endSubmit: () => void;
  /** Nombre de tentatives de soumission */
  submitAttempts: number;
}

interface ValidationResult {
  /**
   * Faux uniquement en cas de menace reelle : injection, contenu malveillant,
   * ou debit anormal. Un format inhabituel n'entre PAS dans ce verdict : le
   * client doit toujours pouvoir enregistrer son dossier.
   */
  isValid: boolean;
  /** Motifs de refus. Vide si `isValid`. */
  errors: string[];
  /** Remarques de forme, consultatives : elles n'arretent rien. */
  warnings: string[];
  securityIssues: SecurityIssue[];
}

interface SecurityIssue {
  type: 'sql_injection' | 'prompt_injection' | 'xss' | 'rate_limit' | 'csrf' | 'invalid_input';
  field?: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Hook pour sécuriser les formulaires contre les attaques courantes
 */
export function useFormSecurity(options: FormSecurityOptions = {}): FormSecurityResult {
  const {
    enableCsrf = true,
    detectSqlInjection: detectSql = true,
    detectPromptInjection: detectPrompt = false,
    enableRateLimit = true,
    rateLimitIdentifier = 'anonymous',
    rateLimitAction = 'formSubmit',
    onSecurityViolation,
  } = options;

  const [csrfToken, setCsrfTokenState] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const lastSubmitTime = useRef<number>(0);

  // Initialiser le token CSRF au montage
  useEffect(() => {
    if (enableCsrf) {
      let token = getCsrfToken();
      if (!token) {
        token = setCsrfToken();
      }
      setCsrfTokenState(token);
    }
  }, [enableCsrf]);

  /**
   * Valide les données du formulaire pour détecter les attaques
   */
  const validateFormSecurity = useCallback((data: object): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const securityIssues: SecurityIssue[] = [];

    // 1. Vérification du rate limiting
    if (enableRateLimit) {
      const { allowed, retryAfter } = checkRateLimit(rateLimitAction, rateLimitIdentifier);
      if (!allowed) {
        securityIssues.push({
          type: 'rate_limit',
          message: `Trop de soumissions. Réessayez dans ${retryAfter} secondes.`,
          severity: 'medium'
        });
        errors.push(`Trop de soumissions. Veuillez patienter ${retryAfter} secondes.`);
      }
    }

    // 2. Convertir les données en chaîne pour l'analyse
    const jsonData = JSON.stringify(data);

    // 3. Détection d'injection SQL
    if (detectSql && detectSqlInjection(jsonData)) {
      securityIssues.push({
        type: 'sql_injection',
        message: 'Tentative d\'injection SQL détectée',
        severity: 'critical'
      });
      errors.push('Les données contiennent des caractères non autorisés.');
      onSecurityViolation?.('sql_injection', 'Tentative d\'injection SQL dans le formulaire');
    }

    // 4. Détection d'injection de prompt (pour les champs texte libre)
    if (detectPrompt) {
      const promptResult = detectPromptInjection(jsonData);
      if (promptResult.detected) {
        securityIssues.push({
          type: 'prompt_injection',
          message: 'Tentative d\'injection de prompt détectée',
          severity: promptResult.riskLevel === 'high' ? 'critical' : 'medium'
        });
        if (promptResult.riskLevel === 'high') {
          errors.push('Le contenu soumis contient des instructions non autorisées.');
          onSecurityViolation?.('prompt_injection', `Patterns détectés: ${promptResult.patterns.join(', ')}`);
        }
      }
    }

    // 5. Détection de contenu malveillant (XSS)
    if (containsMaliciousContent(jsonData)) {
      securityIssues.push({
        type: 'xss',
        message: 'Contenu potentiellement malveillant détecté',
        severity: 'high'
      });
      errors.push('Le contenu soumis contient des éléments non autorisés.');
      onSecurityViolation?.('xss', 'Contenu XSS détecté dans le formulaire');
    }

    // 6. Validation des champs spécifiques
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        // Vérifier les emails
        if (key.toLowerCase().includes('email') && value && !isValidEmail(value)) {
          securityIssues.push({
            type: 'invalid_input',
            field: key,
            message: 'Format d\'email invalide',
            severity: 'low'
          });
          warnings.push(`L'email "${key}" a une forme inhabituelle.`);
        }

        // Vérifier les téléphones
        if ((key.toLowerCase().includes('phone') || key.toLowerCase().includes('telephone')) && value && !isValidPhoneNumber(value)) {
          securityIssues.push({
            type: 'invalid_input',
            field: key,
            message: 'Format de téléphone invalide',
            severity: 'low'
          });
          warnings.push(`Le numéro "${key}" a une forme inhabituelle.`);
        }
      }

      // Vérifier les montants
      if ((key.toLowerCase().includes('amount') || key.toLowerCase().includes('montant') || key.toLowerCase().includes('price')) && value !== undefined && value !== '') {
        if (!isValidAmount(value)) {
          securityIssues.push({
            type: 'invalid_input',
            field: key,
            message: 'Montant invalide',
            severity: 'low'
          });
          warnings.push(`Le montant "${key}" a une forme inhabituelle.`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      securityIssues
    };
  }, [enableRateLimit, rateLimitAction, rateLimitIdentifier, detectSql, detectPrompt, onSecurityViolation]);

  /**
   * Sanitise les données du formulaire
   */
  const sanitizeFormInput = useCallback(<T extends object>(data: T): T => {
    return sanitizeFormData(data);
  }, []);

  /**
   * Démarre une soumission (protection anti-double soumission)
   */
  const startSubmit = useCallback((): boolean => {
    const now = Date.now();
    
    // Empêcher les soumissions trop rapprochées (< 1 seconde)
    if (now - lastSubmitTime.current < 1000) {
      console.warn('🚨 Double soumission détectée');
      return false;
    }

    if (isSubmitting) {
      console.warn('🚨 Soumission déjà en cours');
      return false;
    }

    setIsSubmitting(true);
    setSubmitAttempts(prev => prev + 1);
    lastSubmitTime.current = now;
    return true;
  }, [isSubmitting]);

  /**
   * Termine une soumission
   */
  const endSubmit = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  return {
    csrfToken,
    validateFormSecurity,
    sanitizeFormInput,
    isSubmitting,
    startSubmit,
    endSubmit,
    submitAttempts
  };
}

/**
 * Hook simplifié pour la protection des formulaires simples
 */
export function useSimpleFormSecurity() {
  return useFormSecurity({
    enableCsrf: true,
    detectSqlInjection: true,
    detectPromptInjection: false,
    enableRateLimit: true
  });
}

/**
 * Hook pour les formulaires avec champs de texte libre (chat, commentaires)
 */
export function useTextFormSecurity(userId?: string) {
  return useFormSecurity({
    enableCsrf: true,
    detectSqlInjection: true,
    detectPromptInjection: true,
    enableRateLimit: true,
    rateLimitIdentifier: userId || 'anonymous',
    rateLimitAction: 'formSubmit'
  });
}

export default useFormSecurity;
